import { el, btn } from '@/ui/dom';
import { sfxCollect, sfxUiTick } from '@/audio/sfx';
import { hapticImpact } from '@/ui/haptics';
import { openSheet, sheetInput, type SheetHandle } from '@/ui/social/sheet';
import { colorSwatchRow, ridButton } from '@/ui/social/clubUi';
import {
  HELMET_DESIGN_NAMES,
  captainBadgeSvg,
  helmetEl,
  helmetLabel,
  helmetSvg,
} from '@/ui/social/helmetArt';
import { crestEl, crestIconSvg, crestLabel } from '@/ui/social/crestArt';
import {
  CREST_ICONS,
  CREST_ICON_NAMES,
  CREW_MAX,
  CREW_NAME_MAX,
  HELMET_DESIGNS,
  clubHex,
  createCrewMessage,
  friendRequestMessage,
  inviteMessage,
  isValidCrewName,
  tidyCrewName,
  type Crest,
  type CrewMember,
  type FriendRow,
  type Helmet,
} from '@/social/crewLogic';
import type { SocialResult } from '@/social/SocialService';
import { REPORT_FLAG_SVG } from '@/ui/social/playerSheet';
import { skillTier } from '@/core/skillPoints';
import { USERNAME_MAX } from '@/account/racerNames';

/**
 * crewSheets.ts — every RACER CLUB modal (M140 as "Race Crew"; M141 renamed
 * it and moved the buttons to the Racer ID dashboard's style, ui/social/
 * clubUi.ts), built on ui/social/sheet.ts: the helmet picker, the crest
 * picker (M142), the invite sheet, member actions, club settings and the
 * club / friend link prompts. Each one is a function that opens the sheet and
 * resolves through callbacks — no hidden state outlives the sheet. (M142: the
 * club race chooser moved to ui/social/clubRaceSheet.ts.)
 *
 * Copy follows the house rules: gold = affirmative, blue = share, red only on
 * destructive actions (which route through ui/confirm.ts), no em dashes, and
 * (M141, owner) online seats are filled by "racers": never name AI on screen.
 */

const SHARE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<circle cx="6" cy="12" r="2.6"/><circle cx="17.5" cy="5.6" r="2.6"/><circle cx="17.5" cy="18.4" r="2.6"/>' +
  '<path d="m8.3 10.9 6.9-3.9 1 1.7-6.9 3.9zM8.3 13.1l6.9 3.9 1-1.7-6.9-3.9z"/></svg>';
const HELMET_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 15.2C3.4 9.6 7.2 4.4 13 4.2c4.6-.2 7.7 3.1 7.9 7v2c0 1.5-1.1 2.6-2.6 2.6h-4.8c-1.8 0-3 .7-4 1.9H6.4c-1.3 0-2.2-1-2.4-2.5Zm9.2-5.4c2.7-.9 5.4-.4 6.3 1.3l.1 1.4c-2.3-.2-4.6-.1-6.7.4-.4-1.1-.3-2.2.3-3.1Z"/></svg>';
const BELL_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 22a2.5 2.5 0 0 0 2.4-1.8H9.6A2.5 2.5 0 0 0 12 22Zm7-5.5-1.6-1.7V10a5.4 5.4 0 0 0-4.2-5.3V4a1.2 1.2 0 1 0-2.4 0v.7A5.4 5.4 0 0 0 6.6 10v4.8L5 16.5V18h14v-1.5Z"/></svg>';
const ADD_PERSON_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<circle cx="9" cy="8.2" r="3.6"/><path d="M2.4 19.6c0-3.5 3-5.9 6.6-5.9s6.6 2.4 6.6 5.9v.8H2.4z"/>' +
  '<path d="M18.2 7.4h1.8v2.8h2.8V12H20v2.8h-1.8V12h-2.8v-1.8h2.8z"/></svg>';
const SHIELD_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 1.8 21 4.6v6.1c0 5.5-3.8 9.9-9 11.5-5.2-1.6-9-6-9-11.5V4.6z" opacity=".35"/>' +
  '<path d="M12 4.6 18.6 6.7v4.2c0 4.1-2.8 7.4-6.6 8.7-3.8-1.3-6.6-4.6-6.6-8.7V6.7z"/></svg>';

// ---- helmet picker -------------------------------------------------------------------

export interface HelmetPickerOptions {
  current: Helmet;
  /** 'STEP 2 OF 3' in the create flow; 'YOUR CLUB HELMET' otherwise. */
  kicker?: string;
  saveLabel?: string;
  /** Persist; resolve to an error line or null. */
  onSave: (h: Helmet) => Promise<string | null>;
  /** After a successful save (or a skip in the create flow). */
  onDone?: (saved: boolean) => void;
  reducedMotion?: boolean;
  /** Create/join flow: a SKIP link instead of CANCEL. */
  skippable?: boolean;
}

/**
 * PICK YOUR HELMET (M142, owner: "choose primary and secondary colour from a
 * large colour set, then still pick from 7 designs"): a big floating preview
 * with its name, a PRIMARY row and a SECONDARY row of the 24 club colours,
 * and the 7 designs drawn in the colours being picked.
 */
export function openHelmetPicker(o: HelmetPickerOptions): SheetHandle {
  let pick: Helmet = { ...o.current };
  let done = false;
  // Closed by the player (Esc / scrim / ✕) — possibly while a save is still in
  // flight. onDone has then already run once; the late save must not run it
  // again (a second STEP 3 sheet, a second push ask; review round).
  let closed = false;
  const sheet = openSheet({
    kicker: o.kicker ?? 'YOUR CLUB HELMET',
    title: 'PICK YOUR HELMET',
    className: 'crew-sheet-picker crew-sheet-helmet',
    reducedMotion: o.reducedMotion,
    onClose: () => {
      closed = true;
      if (!done) o.onDone?.(false);
    },
  });
  const b = sheet.body;
  const stage = el('div', 'hp-stage', b);
  const preview = el('div', 'hp-preview', stage);
  const label = el('div', 'hp-label', stage);
  label.setAttribute('aria-live', 'polite');
  const controls = el('div', 'hp-controls', b);

  const primary = colorSwatchRow(controls, {
    label: 'PRIMARY',
    value: pick.primary,
    onPick: (v) => {
      pick = { ...pick, primary: v };
      paint();
    },
  });
  const secondary = colorSwatchRow(controls, {
    label: 'SECONDARY',
    value: pick.secondary,
    onPick: (v) => {
      pick = { ...pick, secondary: v };
      paint();
    },
  });

  el('div', 'hp-row-label', controls).textContent = 'DESIGN';
  const designs = el('div', 'hp-designs', controls);
  const tiles: { btn: HTMLButtonElement; art: HTMLSpanElement }[] = [];
  for (let d = 0; d < HELMET_DESIGNS; d++) {
    const name = HELMET_DESIGN_NAMES[d] ?? '';
    const t = el('button', 'hp-design', designs);
    t.type = 'button';
    t.setAttribute('aria-label', name);
    t.title = name;
    const art = el('span', 'hp-design-art', t);
    el('span', 'hp-design-name', t).textContent = name;
    t.addEventListener('click', () => {
      sfxUiTick();
      pick = { ...pick, design: d };
      paint();
    });
    tiles.push({ btn: t, art });
  }

  const err = el('div', 'crew-sheet-error', b);
  const actions = el('div', 'crew-sheet-actions', b);
  const save = ridButton(
    actions,
    { variant: 'gold', icon: HELMET_SVG, label: o.saveLabel ?? 'SAVE HELMET' },
    () => {
      save.el.disabled = true;
      err.textContent = '';
      void o.onSave({ ...pick }).then((e) => {
        if (closed) return;
        if (e) {
          err.textContent = e;
          save.el.disabled = false;
          return;
        }
        done = true;
        sfxCollect();
        hapticImpact();
        sheet.close();
        o.onDone?.(true);
      });
    },
  );
  if (o.skippable) {
    ridButton(
      actions,
      { variant: 'glass', label: 'SKIP FOR NOW', noChevron: true, className: 'crew-sheet-skip' },
      () => sheet.close(),
    );
  }

  // The design tiles redraw only when a COLOUR changes (a design pick just
  // moves the ring), so a tap never rebuilds seven SVGs for nothing.
  let tileColors = '';
  const paint = (): void => {
    preview.innerHTML = helmetSvg(pick, { title: helmetLabel(pick) });
    label.textContent = helmetLabel(pick);
    primary.set(pick.primary);
    secondary.set(pick.secondary);
    const key = `${pick.primary}/${pick.secondary}`;
    tiles.forEach(({ btn: t, art }, i) => {
      if (key !== tileColors) art.innerHTML = helmetSvg({ ...pick, design: i });
      t.classList.toggle('on', i === pick.design);
      t.setAttribute('aria-pressed', String(i === pick.design));
    });
    tileColors = key;
  };
  paint();
  // Keyboard focus on SAVE without scrolling a tall sheet away from its top.
  save.el.focus({ preventScroll: true });
  return sheet;
}

// ---- crest picker (M142) ----------------------------------------------------------------

export interface CrestPickerOptions {
  current: Crest;
  kicker?: string;
  title?: string;
  saveLabel?: string;
  /** Persist (or, in the create flow, just keep the pick); resolve an error
   *  line or null. */
  onSave: (c: Crest) => Promise<string | null>;
  /** After the sheet closes: saved or not, and the crest it ended on. */
  onDone?: (saved: boolean, crest: Crest) => void;
  reducedMotion?: boolean;
}

/**
 * THE CLUB CREST (M142, owner: "an icon the captain / creator chooses, soccer
 * ball by default, then primary and secondary colour like the helmets"): a
 * big preview crest with its name, the 10 icons, and the FIELD + RIM + ICON
 * rows of the club palette.
 */
export function openCrestPicker(o: CrestPickerOptions): SheetHandle {
  let pick: Crest = { ...o.current };
  let saved = false;
  const sheet = openSheet({
    kicker: o.kicker ?? 'CLUB CREST',
    title: o.title ?? 'PICK YOUR CREST',
    className: 'crew-sheet-picker crew-sheet-crest',
    reducedMotion: o.reducedMotion,
    onClose: () => o.onDone?.(saved, { ...pick }),
  });
  const b = sheet.body;
  const stage = el('div', 'hp-stage', b);
  const preview = el('div', 'hp-preview cp-preview', stage);
  const label = el('div', 'hp-label', stage);
  label.setAttribute('aria-live', 'polite');
  const controls = el('div', 'hp-controls', b);

  el('div', 'hp-row-label', controls).textContent = 'ICON';
  const icons = el('div', 'cp-icons', controls);
  const tiles: { btn: HTMLButtonElement; art: HTMLSpanElement }[] = [];
  for (let i = 0; i < CREST_ICONS; i++) {
    const name = CREST_ICON_NAMES[i] ?? '';
    const t = el('button', 'cp-icon', icons);
    t.type = 'button';
    t.setAttribute('aria-label', name);
    t.title = name;
    const art = el('span', 'cp-icon-art', t);
    el('span', 'cp-icon-name', t).textContent = name;
    t.addEventListener('click', () => {
      sfxUiTick();
      pick = { ...pick, icon: i };
      paint();
    });
    tiles.push({ btn: t, art });
  }
  const field = colorSwatchRow(controls, {
    label: 'FIELD',
    value: pick.primary,
    onPick: (v) => {
      pick = { ...pick, primary: v };
      paint();
    },
  });
  const rim = colorSwatchRow(controls, {
    label: 'RIM + ICON',
    value: pick.secondary,
    onPick: (v) => {
      pick = { ...pick, secondary: v };
      paint();
    },
  });

  const err = el('div', 'crew-sheet-error', b);
  const actions = el('div', 'crew-sheet-actions', b);
  const save = ridButton(
    actions,
    { variant: 'gold', icon: SHIELD_SVG, label: o.saveLabel ?? 'SAVE CREST' },
    () => {
      save.el.disabled = true;
      err.textContent = '';
      void o.onSave({ ...pick }).then((e) => {
        if (e) {
          err.textContent = e;
          save.el.disabled = false;
          return;
        }
        saved = true;
        sfxCollect();
        hapticImpact();
        sheet.close();
      });
    },
  );

  let tileColors = '';
  const paint = (): void => {
    const name = crestLabel(pick);
    preview.replaceChildren(crestEl(pick, 'crew-crest cp-crest', name));
    label.textContent = name;
    field.set(pick.primary);
    rim.set(pick.secondary);
    const key = `${pick.primary}/${pick.secondary}`;
    tiles.forEach(({ btn: t, art }, i) => {
      if (key !== tileColors) {
        art.innerHTML = crestIconSvg(i, clubHex(pick.secondary), {
          fieldHex: clubHex(pick.primary),
        });
        t.style.setProperty('--cp-field', clubHex(pick.primary));
        t.style.setProperty('--cp-rim', clubHex(pick.secondary));
      }
      t.classList.toggle('on', i === pick.icon);
      t.setAttribute('aria-pressed', String(i === pick.icon));
    });
    tileColors = key;
  };
  paint();
  // Keyboard focus on SAVE without scrolling a tall sheet away from its top.
  save.el.focus({ preventScroll: true });
  return sheet;
}

// ---- invite sheet ---------------------------------------------------------------------

export interface InviteSheetOptions {
  crewName: string;
  code: string;
  kicker?: string;
  /** Friends who could be invited (not already in this crew). */
  friends: FriendRow[];
  onShareLink: () => Promise<'shared' | 'copied' | 'dismissed'>;
  onCopyCode: () => Promise<boolean>;
  onInvite: (racerId: string) => Promise<SocialResult>;
  onDone?: () => void;
  reducedMotion?: boolean;
}

export function openInviteSheet(o: InviteSheetOptions): SheetHandle {
  const sheet = openSheet({
    kicker: o.kicker ?? 'BUILD YOUR CLUB',
    title: 'INVITE RACERS',
    className: 'crew-sheet-invite',
    reducedMotion: o.reducedMotion,
    onClose: () => o.onDone?.(),
  });
  const b = sheet.body;
  const top = el('div', 'iv-top', b);

  // 1. The fastest path: share the club link (opens the native sheet).
  const shareBox = el('div', 'iv-share', top);
  ridButton(
    shareBox,
    { variant: 'share', icon: SHARE_SVG, label: 'SHARE CLUB LINK', className: 'iv-share-btn' },
    () => {
      void o.onShareLink().then((how) => {
        if (how === 'copied') shareNote.textContent = 'LINK COPIED. SEND IT ANYWHERE';
        else if (how === 'shared') shareNote.textContent = 'SENT!';
      });
    },
  );
  const shareNote = el('div', 'iv-note', shareBox);
  shareNote.setAttribute('aria-live', 'polite');
  const codeRow = el('div', 'iv-code', shareBox);
  el('span', 'iv-code-label', codeRow).textContent = 'CLUB CODE';
  el('span', 'iv-code-value', codeRow).textContent = o.code;
  const copy = btn('COPY', 'btn btn-secondary iv-code-copy', codeRow, () => {
    void o.onCopyCode().then((ok) => {
      copy.textContent = ok ? 'COPIED' : 'COPY';
    });
  });

  // 2. By Racer ID.
  const byId = el('div', 'iv-byid', top);
  el('div', 'crew-or', byId).textContent = 'OR INVITE BY RACER ID';
  const row = el('div', 'crew-inline-form', byId);
  const input = sheetInput(row, {
    className: 'crew-input',
    placeholder: 'RACER ID',
    maxLength: USERNAME_MAX,
    label: 'Racer ID to invite',
  });
  const status = el('div', 'crew-status', byId);
  status.setAttribute('aria-live', 'polite');
  const send = btn('SEND', 'btn btn-primary crew-inline-btn', row, () => submit());
  const submit = (): void => {
    const v = input.value.trim();
    if (!v) {
      input.focus();
      return;
    }
    send.disabled = true;
    status.className = 'crew-status';
    status.textContent = 'SENDING…';
    void o.onInvite(v).then((r) => {
      send.disabled = false;
      const ok = r.status === 'sent' || r.status === 'pending';
      status.className = `crew-status ${ok ? 'ok' : 'bad'}`;
      status.textContent = inviteMessage(r.status, r.name ?? v);
      if (ok) input.value = '';
    });
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submit();
  });

  // 3. From your friends (one tap each).
  const free = o.friends.filter((f) => !f.sameCrew);
  if (free.length > 0) {
    const fr = el('div', 'iv-friends', b);
    el('div', 'crew-section-label', fr).textContent = 'YOUR FRIENDS';
    const list = el('div', 'iv-friend-list', fr);
    for (const f of free) {
      const r = el('div', 'iv-friend', list);
      r.appendChild(helmetEl(f.helmet, 'crew-helmet crew-helmet-sm'));
      const t = el('div', 'iv-friend-text', r);
      el('div', 'iv-friend-name', t).textContent = f.name.toUpperCase();
      el('div', 'iv-friend-sub', t).textContent = f.crewName
        ? `IN ${f.crewName.toUpperCase()}`
        : 'NO CLUB YET';
      const act = ridButton(
        r,
        {
          variant: 'gold',
          label: f.invited ? 'INVITED' : 'INVITE',
          small: true,
          noChevron: true,
          className: 'iv-friend-btn',
        },
        () => {
          act.el.disabled = true;
          void o.onInvite(f.name).then((res) => {
            const ok = res.status === 'sent' || res.status === 'pending';
            act.setLabel(ok ? 'INVITED' : 'RETRY');
            act.el.disabled = ok;
            if (!ok) {
              status.className = 'crew-status bad';
              status.textContent = inviteMessage(res.status, f.name);
            }
          });
        },
      );
      if (f.invited) act.el.disabled = true;
    }
  }

  el('div', 'crew-fine', b).textContent =
    `Anyone with the link or code can join while there's room (${CREW_MAX} max). Any member can invite.`;
  const actions = el('div', 'crew-sheet-actions', b);
  ridButton(actions, { variant: 'glass', label: 'DONE', noChevron: true }, () => sheet.close());
  return sheet;
}

// ---- member actions -------------------------------------------------------------------------

export interface MemberSheetOptions {
  member: CrewMember;
  iAmCaptain: boolean;
  onAddFriend: () => Promise<SocialResult>;
  onMakeCaptain: () => void;
  onRemove: () => void;
  /** M156: REPORT / BLOCK this member (App Store 1.2) — opens the racer sheet. */
  onModerate?: () => void;
  reducedMotion?: boolean;
}

export function openMemberSheet(o: MemberSheetOptions): SheetHandle {
  const m = o.member;
  const sheet = openSheet({
    kicker: m.role === 'captain' ? 'CLUB CAPTAIN' : 'CLUB MEMBER',
    title: m.name.toUpperCase(),
    className: 'crew-sheet-member',
    reducedMotion: o.reducedMotion,
  });
  const b = sheet.body;
  const card = el('div', 'ms-card', b);
  card.appendChild(helmetEl(m.helmet, 'crew-helmet ms-helmet'));
  const stats = el('div', 'ms-stats', card);
  const tier = skillTier(m.xp);
  const stat = (v: string, l: string): void => {
    const s = el('div', 'ms-stat', stats);
    el('div', 'ms-stat-v', s).textContent = v;
    el('div', 'ms-stat-l', s).textContent = l;
  };
  stat(tier.name, 'RANK');
  stat(m.xp.toLocaleString('en-US'), 'XP');
  stat(String(m.wins), 'WINS');
  stat(m.week.toLocaleString('en-US'), 'THIS WEEK');

  const actions = el('div', 'crew-sheet-actions crew-sheet-stack', b);
  const status = el('div', 'crew-status', b);
  if (!m.me && !m.friend) {
    const add = ridButton(
      actions,
      { variant: 'gold', icon: ADD_PERSON_SVG, label: 'ADD FRIEND' },
      () => {
        add.el.disabled = true;
        void o.onAddFriend().then((r) => {
          const ok = ['sent', 'accepted', 'already', 'pending'].includes(r.status);
          status.className = `crew-status ${ok ? 'ok' : 'bad'}`;
          status.textContent = friendRequestMessage(r.status, m.name);
          add.setLabel(ok ? 'REQUEST SENT' : 'ADD FRIEND');
          add.el.disabled = ok;
        });
      },
    );
  }
  if (o.iAmCaptain && !m.me) {
    const cap = ridButton(
      actions,
      { variant: 'glass', icon: captainBadgeSvg(), label: 'MAKE CAPTAIN', className: 'ms-captain' },
      () => {
        sheet.close();
        o.onMakeCaptain();
      },
    );
    cap.el.querySelector('.rid-btn-ic')?.classList.add('ms-captain-ic');
    btn('REMOVE FROM CLUB', 'btn btn-danger-ghost', actions, () => {
      sheet.close();
      o.onRemove();
    });
  }
  if (actions.childElementCount === 0) {
    el('div', 'crew-fine', b).textContent = m.friend
      ? "You're friends. Race together with a club race."
      : '';
  }
  if (!m.me && o.onModerate) {
    const onModerate = o.onModerate;
    moderateLink(b, () => onModerate());
  }
  return sheet;
}

/** M156: the quiet REPORT / BLOCK link every person-card carries. */
export function moderateLink(parent: HTMLElement, onClick: () => void, label = 'REPORT OR BLOCK'): HTMLButtonElement {
  const link = el('button', 'mod-link', parent);
  link.type = 'button';
  el('span', 'mod-link-ic', link).innerHTML = REPORT_FLAG_SVG;
  el('span', '', link).textContent = label;
  link.addEventListener('click', () => {
    sfxUiTick();
    onClick();
  });
  return link;
}

// ---- club settings (captain) ------------------------------------------------------------------

export interface CrewSettingsOptions {
  crewName: string;
  /** M142: the club's crest (CHANGE CREST opens the crest picker). */
  crest: Crest;
  /** Close settings and open the crest picker (the screen owns the save). */
  onChangeCrest: () => void;
  onCheckName: (name: string) => Promise<boolean | null>;
  onRename: (name: string) => Promise<SocialResult>;
  onResetLink: () => Promise<SocialResult>;
  reducedMotion?: boolean;
}

export function openCrewSettings(o: CrewSettingsOptions): SheetHandle {
  const sheet = openSheet({
    kicker: 'CAPTAIN ONLY',
    title: 'CLUB SETTINGS',
    className: 'crew-sheet-settings',
    reducedMotion: o.reducedMotion,
  });
  const b = sheet.body;
  el('div', 'crew-section-label', b).textContent = 'CLUB CREST';
  const crestRow = el('div', 'cs-crest', b);
  const crestName = crestLabel(o.crest);
  crestRow.appendChild(crestEl(o.crest, 'crew-crest cs-crest-art', crestName));
  el('div', 'cs-crest-name', crestRow).textContent = crestName;
  ridButton(
    crestRow,
    {
      variant: 'glass',
      icon: SHIELD_SVG,
      label: 'CHANGE CREST',
      small: true,
      noChevron: true,
      className: 'cs-crest-btn',
    },
    () => o.onChangeCrest(),
  );

  el('div', 'crew-section-label', b).textContent = 'CLUB NAME';
  const row = el('div', 'crew-inline-form', b);
  const input = sheetInput(row, {
    className: 'crew-input',
    maxLength: CREW_NAME_MAX,
    value: o.crewName,
    label: 'Club name',
  });
  const status = el('div', 'crew-status', b);
  status.textContent = '3 TO 18 LETTERS, NUMBERS OR SPACES';
  const save = btn('RENAME', 'btn btn-primary crew-inline-btn', row, () => {
    const v = tidyCrewName(input.value).trim();
    if (v === o.crewName) return;
    if (!isValidCrewName(v)) {
      status.className = 'crew-status bad';
      status.textContent = createCrewMessage('invalid');
      return;
    }
    save.disabled = true;
    void o.onRename(v).then((r) => {
      save.disabled = false;
      if (r.status === 'ok') {
        status.className = 'crew-status ok';
        status.textContent = 'RENAMED';
        o.crewName = v;
      } else {
        status.className = 'crew-status bad';
        status.textContent =
          r.status === 'rate'
            ? 'Lots of changes for one hour. Try again a little later.'
            : createCrewMessage(r.status === 'not_captain' ? 'offline' : r.status);
      }
    });
  });
  let seq = 0;
  input.addEventListener('input', () => {
    const v = tidyCrewName(input.value);
    if (v !== input.value) input.value = v;
    const mine = ++seq;
    if (!isValidCrewName(v.trim())) {
      status.className = 'crew-status';
      status.textContent = '3 TO 18 LETTERS, NUMBERS OR SPACES';
      return;
    }
    window.setTimeout(() => {
      if (mine !== seq) return;
      void o.onCheckName(v.trim()).then((free) => {
        if (mine !== seq) return;
        status.className = `crew-status ${free === false ? 'bad' : free ? 'ok' : ''}`;
        status.textContent = free === false ? '✗ NAME TAKEN' : free ? '✓ AVAILABLE' : '';
      });
    }, 450);
  });

  el('div', 'crew-section-label', b).textContent = 'CLUB LINK';
  const linkRow = el('div', 'cs-link', b);
  el('div', 'crew-fine', linkRow).textContent =
    'Make a new club link and code. The old ones stop working right away.';
  const reset = ridButton(
    linkRow,
    {
      variant: 'glass',
      label: 'NEW INVITE LINK',
      small: true,
      noChevron: true,
      className: 'cs-reset',
    },
    () => {
      reset.el.disabled = true;
      void o.onResetLink().then((r) => {
        reset.el.disabled = false;
        reset.setLabel(r.status === 'ok' ? `NEW CODE: ${r.code ?? ''}` : 'TRY AGAIN');
      });
    },
  );
  const actions = el('div', 'crew-sheet-actions', b);
  ridButton(actions, { variant: 'glass', label: 'DONE', noChevron: true }, () => sheet.close());
  return sheet;
}

// ---- link prompts -------------------------------------------------------------------------------

export interface CrewLinkPromptOptions {
  crewName: string;
  /** M142: the club's crest (icon + colours). */
  crest: Crest;
  members: number;
  captain: string;
  /** The player's CURRENT club (joining means leaving it), if any. */
  leaving?: string;
  /** Web non-owner: the prompt sells the game instead of joining. */
  locked?: { line: string; buyLabel: string; onBuy: () => void; onLogIn?: () => void };
  onJoin: () => Promise<string | null>;
  onDismiss: () => void;
  reducedMotion?: boolean;
}

export function openCrewLinkPrompt(o: CrewLinkPromptOptions): SheetHandle {
  let handled = false;
  const sheet = openSheet({
    kicker: "YOU'RE INVITED",
    title: `JOIN ${o.crewName.toUpperCase()}?`,
    className: 'crew-sheet-link',
    reducedMotion: o.reducedMotion,
    onClose: () => {
      if (!handled) o.onDismiss();
    },
  });
  const b = sheet.body;
  const card = el('div', 'lp-card', b);
  card.appendChild(crestEl(o.crest, 'crew-crest lp-crest', crestLabel(o.crest)));
  const t = el('div', 'lp-text', card);
  el('div', 'lp-name', t).textContent = o.crewName.toUpperCase();
  el('div', 'lp-meta', t).textContent = `${o.members}/${CREW_MAX} MEMBERS`;
  if (o.captain) {
    const cap = el('div', 'lp-captain', t);
    el('span', 'lp-captain-ic', cap).innerHTML = captainBadgeSvg();
    el('span', '', cap).textContent = `CAPTAIN ${o.captain.toUpperCase()}`;
  }
  const err = el('div', 'crew-sheet-error', b);
  const actions = el('div', 'crew-sheet-actions', b);
  if (o.locked) {
    // The reason sits ABOVE the buttons (read first, then act).
    const why = el('div', 'crew-fine', b);
    why.textContent = o.locked.line;
    b.insertBefore(why, err);
    ridButton(actions, { variant: 'gold', label: o.locked.buyLabel }, () => {
      handled = true;
      sheet.close();
      o.locked?.onBuy();
    });
    if (o.locked.onLogIn) {
      btn('ALREADY OWN IT? LOG IN', 'btn btn-ghost', actions, () => {
        handled = true;
        sheet.close();
        o.locked?.onLogIn?.();
      });
    }
    return sheet;
  }
  if (o.leaving) {
    el('div', 'lp-warn', b).textContent =
      `You're in ${o.leaving.toUpperCase()}. Joining means leaving it, since you can only be in one club.`;
  }
  const join = ridButton(
    actions,
    { variant: 'gold', icon: SHIELD_SVG, label: o.leaving ? 'SWITCH CLUBS' : 'JOIN CLUB' },
    () => {
      join.el.disabled = true;
      err.textContent = '';
      void o.onJoin().then((e) => {
        if (e) {
          err.textContent = e;
          join.el.disabled = false;
          return;
        }
        handled = true;
        sheet.close();
      });
    },
  );
  ridButton(actions, { variant: 'glass', label: 'NOT NOW', noChevron: true }, () => sheet.close());
  join.el.focus();
  return sheet;
}

export interface FriendLinkPromptOptions {
  name: string;
  helmet: Helmet;
  xp: number;
  onAdd: () => Promise<string | null>;
  onDismiss: () => void;
  reducedMotion?: boolean;
}

export function openFriendLinkPrompt(o: FriendLinkPromptOptions): SheetHandle {
  let handled = false;
  const sheet = openSheet({
    kicker: 'FRIEND LINK',
    title: `ADD ${o.name.toUpperCase()}?`,
    className: 'crew-sheet-link',
    reducedMotion: o.reducedMotion,
    onClose: () => {
      if (!handled) o.onDismiss();
    },
  });
  const b = sheet.body;
  const card = el('div', 'lp-card', b);
  card.appendChild(helmetEl(o.helmet, 'crew-helmet lp-helmet'));
  const t = el('div', 'lp-text', card);
  el('div', 'lp-name', t).textContent = o.name.toUpperCase();
  const tier = skillTier(o.xp);
  const meta = el('div', 'lp-meta', t);
  meta.textContent = `${tier.name} · ${o.xp.toLocaleString('en-US')} XP`;
  meta.style.color = tier.color;
  el('div', 'crew-fine', b).textContent =
    'Friends can invite each other into their clubs and race together.';
  const err = el('div', 'crew-sheet-error', b);
  const actions = el('div', 'crew-sheet-actions', b);
  const add = ridButton(
    actions,
    { variant: 'gold', icon: ADD_PERSON_SVG, label: 'ADD FRIEND' },
    () => {
      add.el.disabled = true;
      void o.onAdd().then((e) => {
        if (e) {
          err.textContent = e;
          add.el.disabled = false;
          return;
        }
        handled = true;
        sheet.close();
      });
    },
  );
  ridButton(actions, { variant: 'glass', label: 'NOT NOW', noChevron: true }, () => sheet.close());
  add.el.focus();
  return sheet;
}

/** "Get a ping when your club starts a race" — the contextual soft-ask before
 *  iOS's own permission prompt (best practice: ask at the moment it matters,
 *  and never burn the one-time system prompt on a cold "allow?"). */
export function openPushSoftAsk(o: {
  crewName: string;
  onYes: () => void;
  onNo: () => void;
  reducedMotion?: boolean;
}): SheetHandle {
  let handled = false;
  const sheet = openSheet({
    kicker: o.crewName.toUpperCase(),
    title: 'NEVER MISS A CLUB RACE',
    className: 'crew-sheet-push',
    reducedMotion: o.reducedMotion,
    onClose: () => {
      if (!handled) o.onNo();
    },
  });
  const b = sheet.body;
  el('div', 'push-bell', b).innerHTML = BELL_SVG;
  el('div', 'crew-fine push-lead', b).textContent =
    "We'll ping your phone when someone in your club starts a race. Nothing else, no spam.";
  const actions = el('div', 'crew-sheet-actions', b);
  ridButton(actions, { variant: 'gold', icon: BELL_SVG, label: 'TURN ON' }, () => {
    handled = true;
    sheet.close();
    o.onYes();
  });
  ridButton(actions, { variant: 'glass', label: 'NOT NOW', noChevron: true }, () => sheet.close());
  return sheet;
}
